import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRecommendationV2ById } from "../api";
import useDeepLinkedPropertySwitch from "./useDeepLinkedPropertySwitch";
import { DEEP_LINK_PROPERTY_STATUS } from "./useDeepLinkedProperty";
import { UNAUTHORIZED_PATH } from "../constants/recommendationDeepLink";

/**
 * Resolves an email deep link's recommendationId to its property and switches the
 * app onto it, so the header shows that hotel (EO-1893). The switch goes through
 * the user's own property list, so a forwarded link cannot select a hotel the
 * user does not hold. An unknown id, or one for a hotel the user cannot access,
 * lands on the Unauthorized page.
 *
 * @param {string|null} recommendationId from the URL; null when no link is active
 * @returns {{ isReady: boolean }} true once the app is on the card's property
 */
const useRecommendationDeepLink = (recommendationId) => {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);

  useEffect(() => {
    setCard(null);
    if (!recommendationId) return;
    let cancelled = false;
    getRecommendationV2ById(recommendationId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.propertyId) {
          // The portal property code is what the property search matches
          // exactly; the card's hotel name is only a fallback hint.
          setCard({
            propertyId: data.propertyId,
            searchText: data.propertyCode || data.cards?.[0]?.hotelName || "",
          });
        } else {
          void navigate(UNAUTHORIZED_PATH, { replace: true });
        }
      })
      .catch((error) => {
        // 401 is the auth interceptor's: it is already sending the user to log in.
        if (cancelled || error?.response?.status === 401) return;
        void navigate(UNAUTHORIZED_PATH, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [recommendationId, navigate]);

  const { isOnDeepLinkedProperty, status } = useDeepLinkedPropertySwitch(
    card?.propertyId ?? null,
    { searchText: card?.searchText }
  );

  useEffect(() => {
    if (status === DEEP_LINK_PROPERTY_STATUS.NOT_FOUND) {
      void navigate(UNAUTHORIZED_PATH, { replace: true });
    }
  }, [status, navigate]);

  return { isReady: Boolean(card) && isOnDeepLinkedProperty };
};

export default useRecommendationDeepLink;
